import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Backs the global top-left back button (BackButtonComponent). Two responsibilities:
 *
 *  1. Track the previous in-app URL so pages reachable from several places (Settings, History)
 *     can return to wherever they were actually opened from.
 *  2. Resolve, for any given path, whether it's a section landing (button hidden) and, if not,
 *     where "back" should go.
 *
 * Feature pages use a FIXED section landing (brainbites → home, a scoring session → the score
 * landing, etc.) so back is predictable regardless of how the user got there. History/Settings/
 * other internal pages use the tracked previous URL, falling back to a section landing when there
 * is no in-app history yet (deep link / hard refresh).
 *
 * Injected in AppComponent's constructor so tracking starts at app boot, before the nav shell
 * (and this button) become visible.
 */
@Injectable({ providedIn: 'root' })
export class BackNavigationService {
  /** The section landing hubs. The back button is hidden on these. */
  static readonly LANDING_HUBS = [
    '/landing-page',
    '/score',
    '/plan-home',
    '/sanctuary',
    '/learn',
    '/community',
  ];

  /**
   * Route prefixes that provide their own in-page back navigation (e.g. the plan
   * page's action menu), so the global back button is hidden there. Matched as an
   * exact path or a `/prefix/…` descendant — so `/plan` and `/plan/x` match, but
   * `/plan-home` (already a landing hub) and `/plan-old` do not.
   */
  static readonly SELF_NAVIGATING_PREFIXES = ['/plan', '/learn', '/score/marginofsafety', '/score/buffer-result'];

  private previousUrl?: string;
  private currentUrl?: string;

  constructor(private router: Router) {
    this.currentUrl = this.normalize(this.router.url);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = this.normalize((event as NavigationEnd).urlAfterRedirects);
        // Ignore no-op re-navigations to the same path so previousUrl stays meaningful.
        if (url !== this.currentUrl) {
          this.previousUrl = this.currentUrl;
          this.currentUrl = url;
        }
      });
  }

  /** Path only — query string and fragment stripped (mirrors BottomNavComponent). */
  private normalize(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  /** True when the given path is a section landing hub, where the back button should be hidden. */
  isLandingRoute(path: string): boolean {
    return BackNavigationService.LANDING_HUBS.includes(this.normalize(path));
  }

  /**
   * True when the global back button should be hidden for the given path — on a
   * section landing hub, or on a self-navigating page (its own in-page back UI).
   */
  isBackButtonHidden(path: string): boolean {
    const p = this.normalize(path);
    if (this.isLandingRoute(p)) {
      return true;
    }
    return BackNavigationService.SELF_NAVIGATING_PREFIXES.some(
      (prefix) => p === prefix || p.startsWith(prefix + '/'),
    );
  }

  /**
   * Where "back" should navigate from the given internal path.
   * Feature pages return a fixed section landing; History/Settings/other internal pages return the
   * previous in-app URL (where they were opened from), falling back to a section landing.
   */
  resolveTarget(path: string): string {
    const p = this.normalize(path);

    const fixedLanding = this.fixedLandingFor(p);
    if (fixedLanding) {
      return fixedLanding;
    }

    // Multi-entry / other internal pages: go back to where the user came from.
    const fallback = this.fallbackFor(p);
    if (this.previousUrl && this.previousUrl !== p) {
      return this.previousUrl;
    }
    return fallback;
  }

  /** Fixed section landing for feature pages, or null if the page uses previous-URL back. */
  private fixedLandingFor(path: string): string | null {
    if (path.startsWith('/brainbites')) {
      return '/landing-page';
    }
    if (path.startsWith('/score/')) {
      return '/score';
    }
    if (path === '/plan' || path.startsWith('/plan/') || path.startsWith('/card-library')) {
      return '/plan-home';
    }
    if (path.startsWith('/sanctuary/')) {
      return '/sanctuary';
    }
    if (path.startsWith('/learn/')) {
      return '/learn';
    }
    // FLIP-314: a logged-in user only reaches the reset-password page from Settings'
    // "Forgot password?" link, and the previous-URL path would drop the fragment - so
    // return to Settings with the password panel open, matching the page's own link.
    if (path === '/reset-password') {
      return '/settings#change-password';
    }
    return null;
  }

  /** Fallback landing for previous-URL pages when there is no in-app history yet. */
  private fallbackFor(path: string): string {
    if (path.startsWith('/history')) {
      return '/score';
    }
    return '/landing-page';
  }
}
